import React, { useMemo, useState } from 'react';
import { TrendingUp, DollarSign, Users, Target, Timer, Sparkles, ArrowUpRight, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

/* -----------------------------------------------------------------
   Number-formatting helpers
----------------------------------------------------------------- */
const fmtCurrency = (n) => {
  if (!isFinite(n)) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};
const fmtInt = (n) => Math.round(n).toLocaleString();

/* -----------------------------------------------------------------
   Baseline uplift constants (from Predictor's proven benchmarks)
----------------------------------------------------------------- */
const UPLIFT = {
  mqlToSql: 3.1, // MQL → SQL uplift multiplier
  closeRate: 1.35, // 35% lift in close rate for signal-scored SQLs
  cycleReduction: 0.28, // 28% shorter cycle
  targetingUplift: 1.42, // 42% more effective ICP targeting
};

/* -----------------------------------------------------------------
   Field input — slider + number combo
----------------------------------------------------------------- */
function Field({ label, unit, value, min, max, step, onChange, testId, accent = '#4ECDC4' }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors">
      <div className="flex items-baseline justify-between mb-3">
        <label className="text-[10px] font-mono uppercase tracking-widest text-white/50">{label}</label>
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-serif italic tabular text-[#F1EDE4]">{value.toLocaleString()}</span>
          {unit && <span className="text-[10px] font-mono text-white/40">{unit}</span>}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        className="w-full h-1 bg-white/10 rounded-full appearance-none cursor-pointer roi-slider"
        style={{
          background: `linear-gradient(90deg, ${accent} 0%, ${accent} ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.1) ${
            ((value - min) / (max - min)) * 100
          }%, rgba(255,255,255,0.1) 100%)`,
        }}
      />
      <div className="flex justify-between text-[9px] font-mono text-white/30 mt-1.5">
        <span>{min.toLocaleString()}</span>
        <span>{max.toLocaleString()}</span>
      </div>
    </div>
  );
}

export default function RoiCalculator() {
  // Hybrid inputs (marketing + sales)
  const [accounts, setAccounts] = useState(2500);
  const [mqlRate, setMqlRate] = useState(6); // % of contacts that become MQL
  const [sqlRate, setSqlRate] = useState(28); // % of MQLs that become SQL
  const [closeRate, setCloseRate] = useState(22); // % of SQLs that close
  const [dealSize, setDealSize] = useState(75000); // $
  const [investment, setInvestment] = useState(120000); // annual Predictor invest

  const calc = useMemo(() => {
    // Baseline (current) — annualised
    const mqls = accounts * (mqlRate / 100) * 12; // per year
    const sqls = mqls * (sqlRate / 100);
    const wins = sqls * (closeRate / 100);
    const revenue = wins * dealSize;

    // With Predictor uplift
    const mqlsP = accounts * UPLIFT.targetingUplift * (mqlRate / 100) * 12;
    const sqlsP = mqlsP * ((sqlRate / 100) * UPLIFT.mqlToSql > 0.85 ? 0.85 : (sqlRate / 100) * UPLIFT.mqlToSql);
    const winsP = sqlsP * (Math.min(0.6, (closeRate / 100) * UPLIFT.closeRate));
    const revenueP = winsP * dealSize;

    const deltaRevenue = revenueP - revenue;
    const roiMultiple = investment > 0 ? deltaRevenue / investment : 0;
    const paybackMonths = deltaRevenue > 0 ? Math.max(0.5, (investment / (deltaRevenue / 12))) : 99;

    return {
      base: { mqls, sqls, wins, revenue },
      lift: { mqls: mqlsP, sqls: sqlsP, wins: winsP, revenue: revenueP },
      delta: {
        mqls: mqlsP - mqls,
        sqls: sqlsP - sqls,
        wins: winsP - wins,
        revenue: deltaRevenue,
      },
      roi: {
        multiple: roiMultiple,
        payback: paybackMonths,
      },
    };
  }, [accounts, mqlRate, sqlRate, closeRate, dealSize, investment]);

  return (
    <section id="roi" className="py-20 lg:py-28 border-t border-white/10 bg-[#050A1F] relative overflow-hidden">
      <div
        className="absolute -top-40 -left-20 w-[600px] h-[600px] rounded-full opacity-40 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.3), transparent 70%)' }}
      />
      <div
        className="absolute -bottom-40 -right-20 w-[700px] h-[700px] rounded-full opacity-40 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.28), transparent 70%)' }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-6 mb-12 lg:mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#FFB020] mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              <span>ROI Calculator · interactive</span>
            </div>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl tracking-tight leading-[1.02] font-medium">
              What&apos;s Predictor worth to <span className="font-serif italic text-[#FFB020]">your pipeline?</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-white/60 text-[15px] leading-relaxed">
              Slide the numbers. See your projected annual uplift, ROI multiple, and payback in months — modelled on Predictor&apos;s benchmarked lifts across 200+ enterprise programs.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Inputs column */}
          <div className="lg:col-span-5 space-y-3">
            <Field
              label="Target accounts / month"
              unit="accts"
              value={accounts}
              min={100}
              max={20000}
              step={100}
              onChange={setAccounts}
              testId="roi-accounts"
              accent="#4ECDC4"
            />
            <Field
              label="Current MQL rate"
              unit="%"
              value={mqlRate}
              min={1}
              max={20}
              step={0.5}
              onChange={setMqlRate}
              testId="roi-mql-rate"
              accent="#4ECDC4"
            />
            <Field
              label="Current MQL → SQL rate"
              unit="%"
              value={sqlRate}
              min={5}
              max={60}
              step={1}
              onChange={setSqlRate}
              testId="roi-sql-rate"
              accent="#4ECDC4"
            />
            <Field
              label="Current SQL close rate"
              unit="%"
              value={closeRate}
              min={5}
              max={50}
              step={1}
              onChange={setCloseRate}
              testId="roi-close-rate"
              accent="#FFB020"
            />
            <Field
              label="Average deal size"
              unit="$"
              value={dealSize}
              min={5000}
              max={500000}
              step={5000}
              onChange={setDealSize}
              testId="roi-deal-size"
              accent="#FFB020"
            />
            <Field
              label="Annual Predictor investment"
              unit="$"
              value={investment}
              min={30000}
              max={600000}
              step={5000}
              onChange={setInvestment}
              testId="roi-investment"
              accent="#FFB020"
            />
          </div>

          {/* Output column */}
          <div className="lg:col-span-7 space-y-4">
            {/* Headline card */}
            <div className="relative bg-gradient-to-br from-[#0F1C40] via-[#0F1C40] to-[#050A1F] border border-[#FFB020]/40 rounded-2xl p-6 lg:p-8 overflow-hidden">
              <div
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.35), transparent 70%)' }}
              />
              <div className="relative">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#FFB020] mb-3">
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span>Projected annual uplift</span>
                </div>
                <div className="flex items-baseline gap-4 flex-wrap">
                  <div className="text-6xl lg:text-8xl font-serif italic tabular text-[#FFB020]" data-testid="roi-delta-revenue">
                    {fmtCurrency(calc.delta.revenue)}
                  </div>
                  <div className="text-[13px] text-white/60">additional closed-won revenue / year</div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-3 gap-6">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">ROI multiple</div>
                    <div className="text-3xl font-serif italic tabular text-[#4ECDC4]" data-testid="roi-multiple">
                      {calc.roi.multiple >= 100 ? '99+×' : `${calc.roi.multiple.toFixed(1)}×`}
                    </div>
                    <div className="text-[10px] text-white/40 mt-1">on ${fmtInt(investment / 1000)}K invest</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Payback</div>
                    <div className="text-3xl font-serif italic tabular text-[#4ECDC4]" data-testid="roi-payback">
                      {calc.roi.payback > 24 ? '24+ mo' : `${calc.roi.payback.toFixed(1)} mo`}
                    </div>
                    <div className="text-[10px] text-white/40 mt-1">time to break-even</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Extra wins</div>
                    <div className="text-3xl font-serif italic tabular text-[#FFB020]" data-testid="roi-extra-wins">
                      +{fmtInt(calc.delta.wins)}
                    </div>
                    <div className="text-[10px] text-white/40 mt-1">deals / year</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed funnel comparison */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 lg:p-8">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-5">
                <Zap className="w-3.5 h-3.5" />
                <span>Funnel comparison · annualised</span>
              </div>

              <div className="space-y-3">
                {[
                  {
                    icon: Users,
                    label: 'MQLs',
                    base: calc.base.mqls,
                    lift: calc.lift.mqls,
                    color: '#4ECDC4',
                  },
                  {
                    icon: Target,
                    label: 'SQLs',
                    base: calc.base.sqls,
                    lift: calc.lift.sqls,
                    color: '#4ECDC4',
                  },
                  {
                    icon: Timer,
                    label: 'Closed wins',
                    base: calc.base.wins,
                    lift: calc.lift.wins,
                    color: '#FFB020',
                  },
                  {
                    icon: DollarSign,
                    label: 'Revenue',
                    base: calc.base.revenue,
                    lift: calc.lift.revenue,
                    color: '#FFB020',
                    isCurrency: true,
                  },
                ].map((row) => {
                  const Icon = row.icon;
                  const pct = row.base > 0 ? Math.min(100, (row.lift / (row.lift + row.base)) * 100) : 50;
                  return (
                    <div key={row.label} className="grid grid-cols-[auto_1fr] gap-4 items-center">
                      <div className="flex items-center gap-2 w-32">
                        <Icon className="w-3.5 h-3.5" style={{ color: row.color }} strokeWidth={1.75} />
                        <span className="text-[12px] text-white/70">{row.label}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 text-[11px] font-mono">
                          <span className="text-white/50 w-20">
                            {row.isCurrency ? fmtCurrency(row.base) : fmtInt(row.base)}
                          </span>
                          <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden relative">
                            <div
                              className="absolute inset-y-0 left-0 bg-white/20"
                              style={{ width: `${pct * 0.4}%` }}
                            />
                            <div
                              className="absolute inset-y-0 left-0 h-full rounded-full transition-all duration-500"
                              style={{ width: `${pct}%`, background: row.color }}
                            />
                          </div>
                          <span className="w-24 text-right font-semibold" style={{ color: row.color }}>
                            {row.isCurrency ? fmtCurrency(row.lift) : fmtInt(row.lift)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-3 text-[11px]">
                <span className="text-white/50">
                  Based on <span className="text-[#4ECDC4] font-mono">3.1×</span> MQL→SQL uplift +{' '}
                  <span className="text-[#FFB020] font-mono">1.35×</span> close-rate lift
                </span>
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-1.5 text-[#FFB020] hover:text-[#4ECDC4] font-mono uppercase tracking-widest text-[10px]"
                >
                  Model your program with a strategist
                  <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
